import { faServer, faTrashCanXmark } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Autocomplete,
  BottomNavigation,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch
} from "@mui/material"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useApiGet, useApiPost } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"

import { default as Button, default as SubmitButton } from "components/Button"
import NonFormikSidebar from "components/sidedrawer/NonFormikSidebar"
import { StyledStickyButtonBox } from "components/sidedrawer/Sidebar.styled"
import SidebarHeader from "components/sidedrawer/SidebarHeader"
import theme from "css/theme/light"
import { FieldArray, Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { t } from "i18next"
import { deviceValidationSchema } from "pages/systemadmin/pages/devices/components/validationSchema"
import { useContext, useEffect, useRef, useState } from "react"
import { SidebarEquipmentForm } from "../../equipment/components/SidebarEquipmentForm"

const serviceLevels = [
  { name: "Inget ", id: null },
  { name: "80", id: 80 },
  { name: "60", id: 60 },
  { name: "40", id: 40 },
  { name: "20", id: 20 }
]

const groupedData = (data) =>
  data.reduce((groups, item) => {
    const key = item.equipmentType
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
    return groups
  }, {})

export const SidebarDeviceForm = ({ onSubmit, result, updateResult }) => {
  const initialValues = result?.data || {
    equipmentId: "",
    companyId: 0,
    status: "",
    equipmentTypeId: null,
    subscriptionTime: null,
    equipmentDescription: "",
    mountedOnDeviceId: null,
    mountedOnStorageId: null,
    hasConnectToDevice: false,
    supplements: []
  }
  const [selectedGroup, setSelectedGroup] = useState("")

  const appContext = useContext(AppContext)

  const formRef = useRef()
  const handleSubmit = async (values) => {
    onSubmit(values)
  }
  const [addEquipmentResult, addEquipment] = useApiPost()
  const [technicalPackagesResult, getTechnicalPackages] = useApiGet({})
  const [equipmentsNotConnectedResult, getEquipmentsNotConnected] = useApiGet()
  const [searchValue, setSearchValue] = useState("")
  const [addedEquipment, setAddedEquipment] = useState(null)

  useEffect(() => {
    getTechnicalPackages(apiEndpoints.systemadmindeviceregistry.listtechnicalpackage)
    getEquipmentsNotConnected(apiEndpoints.systemadmindeviceregistry.listequipmentsnotconnectedtodevice)
  }, [getEquipmentsNotConnected, getTechnicalPackages])

  const handleEquipmentSubmit = (values) => {
    addEquipment(apiEndpoints.systemadmindeviceregistry.addequipment, values)
    setAddedEquipment(values.equipmentId)
  }

  useEffect(() => {
    if (addEquipmentResult.status === 2) {
      appContext.toggleSideBarMulti("equipment", false)
      getEquipmentsNotConnected(apiEndpoints.systemadmindeviceregistry.listequipmentsnotconnectedtodevice)
    }
  }, [addEquipmentResult.status, addedEquipment, getEquipmentsNotConnected])

  useEffect(() => {
    if (equipmentsNotConnectedResult.status === 2 && addedEquipment !== null) {
      const newList = formRef?.current?.values.equipments ? [...formRef?.current?.values.equipments] : []
      const index = equipmentsNotConnectedResult?.data?.findIndex(
        (item) => item.equipmentId === addedEquipment
      )
      if (index !== -1) {
        newList.push(equipmentsNotConnectedResult?.data[index])
        formRef?.current?.setFieldValue("equipments", newList)
        setAddedEquipment(null)
      }
    }
  }, [addedEquipment, equipmentsNotConnectedResult?.data, equipmentsNotConnectedResult.status])

  return (
    <>
      <NonFormikSidebar
        buttonChildren={
          <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
            <SubmitButton
              result={updateResult}
              style={{ width: "100%" }}
              color="primary"
              onClick={() => {
                formRef.current?.handleSubmit()
              }}
              variant="contained">
              {t("global.save_changes", "Save changes")}
            </SubmitButton>
            <Button
              variant="outlined"
              onClick={() => {
                appContext.toggleSideBarMulti("device", false)

                if (typeof onSubmit === "function" && formRef?.current?.resetForm) {
                  formRef.current.resetForm()
                }
              }}>
              {t("global.buttons.cancel", "Cancel")}
            </Button>
          </StyledStickyButtonBox>
        }
        sidebarId="device">
        <>
          <Formik
            innerRef={formRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnChange={false}
            validationSchema={deviceValidationSchema}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              handleSubmit,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Box pt={10} pb={5}>
                  <SidebarHeader
                    icon={faServer}
                    backgroundColor={theme.palette.primary[300]}
                    color={theme.palette.primary[800]}
                    size="lg"
                    title={t("global.settings", "Settings")}
                  />

                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                onChange={(item) => {
                                  const checked = item.target.checked
                                  setFieldValue("isActive", checked)
                                }}
                                checked={values.isActive}
                              />
                            }
                            label={t("global.active", "Active")}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            autoComplete="off"
                            type="text"
                            fullWidth
                            id="internalDeviceId"
                            label={t("global.internal_number", "Internal number")}
                            value={values.internalDeviceId || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            error={touched.internalDeviceId && errors.internalDeviceId ? true : false}
                            helperText={
                              touched.internalDeviceId && errors.internalDeviceId
                                ? errors.internalDeviceId
                                : null
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            autoComplete="off"
                            fullWidth
                            id="deviceId"
                            label={t("deviation.license_plate", "License plate")}
                            value={values.deviceId || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.deviceId && errors.deviceId ? true : false}
                            helperText={touched.deviceId && errors.deviceId ? errors.deviceId : null}
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {technicalPackagesResult.status === 2 && (
                            <Autocomplete
                              style={{ minWidth: "100%" }}
                              id="technicalPackageId"
                              options={technicalPackagesResult.data}
                              getOptionLabel={(option) => (option.name ? option.name : "")}
                              value={technicalPackagesResult.data.find(
                                (i) => i.id === values.technicalPackageId
                              )}
                              isOptionEqualToValue={(option, value) => {
                                return option.id === value.id
                              }}
                              onChange={(e, value) => {
                                setFieldValue("technicalPackageId", value?.id)
                              }}
                              renderInput={(params) => (
                                <TextField
                                  style={{ background: theme.palette.background.paper }}
                                  {...params}
                                  label={t("sysadmin.select_package", "Select package")}
                                  variant="outlined"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    touched.technicalPackageId && errors.technicalPackageId ? true : false
                                  }
                                  helperText={
                                    touched.technicalPackageId && errors.technicalPackageId
                                      ? errors.technicalPackageId
                                      : null
                                  }
                                />
                              )}
                            />
                          )}
                          <FormControl sx={{ width: "100%", marginTop: 2 }}>
                            <InputLabel id="serviceLevel-label">
                              {t("sysadmin.service_level", "Service level")}
                            </InputLabel>
                            <Select
                              sx={{ width: "100%" }}
                              className="w-100"
                              variant="outlined"
                              labelId="serviceLevel-label"
                              id="serviceLevel"
                              label={t("sysadmin.service_level", "Service level")}
                              value={values.serviceLevel || ""}
                              onChange={(event) => {
                                const value = event.target.value
                                setFieldValue("serviceLevel", value)
                              }}>
                              {serviceLevels &&
                                serviceLevels.map((item) => {
                                  return <MenuItem value={item.id}>{item.name}</MenuItem>
                                })}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            autoComplete="off"
                            minRows={4}
                            multiline
                            fullWidth
                            id="deviceDescription"
                            label={t("global.description", "Description")}
                            value={values.deviceDescription || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.deviceDescription && errors.deviceDescription ? true : false}
                            helperText={
                              touched.deviceDescription && errors.deviceDescription
                                ? errors.deviceDescription
                                : null
                            }
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl>
                            <FormLabel id="simCardSupplier">
                              {t("sysadmin.sim_card_provider", "SIM card provider")}
                            </FormLabel>
                            <RadioGroup
                              row
                              aria-label="simCardSupplier"
                              name="simCardSupplier"
                              value={values.simCardSupplier || ""}
                              onChange={(e) => {
                                const value = e.target.value === "" ? null : Number.parseInt(e.target.value)
                                setFieldValue("simCardSupplier", value)
                              }}>
                              <FormControlLabel
                                value={null}
                                control={<Radio />}
                                label={t("global.none", "None")}
                              />
                              <FormControlLabel
                                value={0}
                                control={<Radio />}
                                label={t("sysadmin.malux", "Malux")}
                              />
                              <FormControlLabel
                                value={1}
                                control={<Radio />}
                                label={t("global.own", "Own")}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            autoComplete="off"
                            fullWidth
                            id="phone"
                            label={t("settings.form.phone", "Phone")}
                            value={values.phone || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            error={touched.phone && errors.phone ? true : false}
                            helperText={touched.phone && errors.phone ? errors.phone : null}
                          />
                        </Grid>
                      </Grid>
                      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
                      <Grid
                        container
                        spacing={2}
                        marginBottom={2}
                        justifyContent={"space-between"}
                        alignItems="center">
                        <Grid item xs={7}>
                          <Typography variant="h4">{t("global.equipment", "Equipment")}</Typography>
                        </Grid>
                        <Grid item xs={5} textAlign="right">
                          <Button onClick={() => appContext.toggleSideBarMulti("equipment", true)}>
                            {t("global.add", "Add")}
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          {equipmentsNotConnectedResult.status === 2 && equipmentsNotConnectedResult.data && (
                            <FormControl fullWidth>
                              <InputLabel id="group-select-label" size="small">
                                {t("global.equipment_type", "Equipment type")}
                              </InputLabel>
                              <Select
                                size="small"
                                labelId="group-select-label"
                                id="group-select"
                                value={selectedGroup}
                                label={t("global.group", "Group")}
                                onChange={(e) => setSelectedGroup(e.target.value)}>
                                <MenuItem value="">{t("global.all", "All")}</MenuItem>
                                {Object.keys(groupedData(equipmentsNotConnectedResult.data)).map((group) => (
                                  <MenuItem key={group} value={group}>
                                    {group}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <TextField
                              size="small"
                              autoComplete="off"
                              fullWidth
                              id="search"
                              label={t("global.search", "Search")}
                              value={searchValue}
                              onChange={(e) => setSearchValue(e.target.value)}
                              onBlur={handleBlur}
                              variant="outlined"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          {equipmentsNotConnectedResult.status === 2 &&
                            equipmentsNotConnectedResult.data &&
                            Object.entries(
                              groupedData(
                                equipmentsNotConnectedResult.data.filter(
                                  (item) =>
                                    (!searchValue ||
                                      (item.equipmentId &&
                                        item.equipmentId
                                          .toLowerCase()
                                          .includes(searchValue.toLowerCase()))) &&
                                    (!selectedGroup || item.equipmentType === selectedGroup)
                                )
                              )
                            ).map(([group, items]) => (
                              <FieldArray name="equipments">
                                {({ push, remove, form }) => (
                                  <>
                                    <Box key={group} mb={2}>
                                      <Typography variant="h5" marginBottom={1}>
                                        {group}
                                      </Typography>
                                      {items.map((equipment, index) => {
                                        const selectedIndex = form.values?.equipments?.findIndex(
                                          (item) => item.equipmentId === equipment.equipmentId
                                        )
                                        const isSelected = selectedIndex !== -1 && selectedIndex !== undefined
                                        return (
                                          <Box style={{ marginY: 15 }} key={equipment.equipmentId || index}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={isSelected}
                                                  onChange={(e) => {
                                                    if (e.target.checked) {
                                                      if (!isSelected) {
                                                        equipment.mountedOnDeviceId = values.id || 0
                                                        push(equipment)
                                                      }
                                                    } else {
                                                      if (isSelected) {
                                                        remove(selectedIndex)
                                                      }
                                                    }
                                                  }}
                                                />
                                              }
                                              label={equipment.equipmentId}
                                            />
                                          </Box>
                                        )
                                      })}
                                    </Box>
                                  </>
                                )}
                              </FieldArray>
                            ))}
                        </Grid>
                      </Grid>
                      {values.equipments && values.equipments.length > 0 ? (
                        <>
                          <Grid container spacing={2} marginBottom={1}>
                            <Grid item xs={3}>
                              <Typography variant="labelSmallBold" color="textSecondary">
                                {t("global.equipment", "Equipment")}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="labelSmallBold" color="textSecondary">
                                {t("global.coupling", "Coupling")}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="labelSmallBold" color="textSecondary">
                                {t("sysadmin.position_prio", "Position Priority")}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="labelSmallBold" color="textSecondary">
                                {t("sysadmin.io_prio", "IO Priority")}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Divider />
                          {errors.equipments && typeof errors.equipments === "string" && (
                            <Typography variant="bodySmall" color="error" sx={{ marginTop: 1 }}>
                              {errors.equipments}
                            </Typography>
                          )}
                          <FieldArray name="equipments">
                            {({ push, remove, form }) => (
                              <>
                                {form.values.equipments &&
                                  form.values.equipments
                                    .filter((equipment) => equipment.mountedOnDeviceId !== null)
                                    .map((equipment, index) => {
                                      const equipmentIndex = form.values.equipments.findIndex(
                                        (item) => item.equipmentId === equipment.equipmentId
                                      )

                                      return (
                                        <>
                                          <Grid
                                            container
                                            spacing={2}
                                            alignItems="center"
                                            key={equipment.id || index}>
                                            <Grid item xs={3}>
                                              <Typography variant="bodyMedium">
                                                {equipment.equipmentType}
                                                {equipment?.supplements?.map((data, index) => (
                                                  <>
                                                    <Typography variant="bodySmall" color="textSecondary">
                                                      {data.alarmType === 2
                                                        ? t("global.immobilizer", "Immobilizer")
                                                        : t("vehiclealarm.alcohol_lock", "Alcohol lock")}{" "}
                                                      - {data.avlId}
                                                    </Typography>
                                                  </>
                                                ))}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                              <Typography variant="bodyMedium">
                                                {equipment.equipmentId}
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={!!equipment.positionPriority}
                                                    onChange={(e) => {
                                                      form.setFieldValue(
                                                        `equipments[${equipmentIndex}].positionPriority`,
                                                        e.target.checked
                                                      )
                                                    }}
                                                  />
                                                }
                                              />
                                            </Grid>
                                            <Grid item xs={2}>
                                              <FormControlLabel
                                                control={
                                                  <Checkbox
                                                    checked={!!equipment.ioPriority}
                                                    onChange={(e) => {
                                                      form.setFieldValue(
                                                        `equipments[${equipmentIndex}].ioPriority`,
                                                        e.target.checked
                                                      )
                                                    }}
                                                  />
                                                }
                                              />
                                            </Grid>
                                            <Grid item xs={1} justifyContent={"end"} display="flex">
                                              <FontAwesomeIcon
                                                icon={faTrashCanXmark}
                                                color={theme.palette.error.main}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                  form.setFieldValue(
                                                    `equipments[${equipmentIndex}].positionPriority`,
                                                    false
                                                  )
                                                  form.setFieldValue(
                                                    `equipments[${equipmentIndex}].ioPriority`,
                                                    false
                                                  )
                                                  form.setFieldValue(
                                                    `equipments[${equipmentIndex}].mountedOnDeviceId`,
                                                    null
                                                  )
                                                }}
                                              />
                                            </Grid>
                                          </Grid>
                                          <Divider style={{ marginTop: 5, marginBottom: 5 }} />
                                        </>
                                      )
                                    })}
                              </>
                            )}
                          </FieldArray>
                        </>
                      ) : (
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          direction="column"
                          gap={4}>
                          <Typography variant="bodySmall">None equipments are selected</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Box marginTop={15}></Box>
                  <Box sx={{ position: "fixed", bottom: 8, left: 0, right: 15 }} elevation={3}>
                    <BottomNavigation
                      sx={{ justifyContent: "flex-end", backgroundColor: "transparent", height: "auto" }}>
                      <Button color="primary" type="submit" result={result}>
                        {t("global.save", "Save")}
                      </Button>
                    </BottomNavigation>
                  </Box>
                </Box>
              </form>
            )}
          </Formik>
        </>
      </NonFormikSidebar>
      <SidebarEquipmentForm onSubmit={handleEquipmentSubmit} result={addEquipmentResult} />
    </>
  )
}
